import {
  faCube,
  faTruck,
  faUser,
  faWeightHanging,
  faCalculator,
} from "../../../utilities/fontawesomeIcons.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { UncontrolledTooltip } from "reactstrap";
import { useAppContext } from "../../../AppProvider.jsx";
import { useDetailsIndexContext } from "../providers/hooks";

export const DetailsCostsWeight: React.FC = () => {
  const { dictionary } = useAppContext();
  const { data } = useDetailsIndexContext();

  // Conversion factor for volumetric weight
  const conversionFactor = undefined;

  // Volumetric weight calculation
  const volumetricWeight = (volume: number, conversionFactor?: number) => {
    if (conversionFactor == null) {
      return 0;
    }

    return volume * conversionFactor;
  };

  const volumetric =
    data.volume != null
      ? volumetricWeight(data.volume, conversionFactor)
      : null;

  // Taxable weight: the greater between actual weight and volumetric weight
  const taxed =
    data.weight != null && volumetric != null
      ? Math.max(data.weight, volumetric)
      : null;

  return (
    <div className="d-flex justify-content-center">
      <div className="row w-100 mb-3 text-center">
        {/* ======================================================
            COLUMN 1: ICONS / METRIC TYPES
            - Weight
            - Volume
            - Volumetric weight
           ====================================================== */}
        <div className="col-2 p-0" style={{ borderRight: "1px solid #c7c9cd" }}>
          {/* Empty header cell */}
          <div className="py-2" style={{ borderBottom: "1px solid #c7c9cd" }}>
            &nbsp;
          </div>

          {/* Weight icon */}
          <div id="weight-icon" className="py-2 cursor-help">
            <FontAwesomeIcon icon={faWeightHanging} />
            <UncontrolledTooltip placement="right" target="weight-icon">
              {dictionary.dimensions.weight}
            </UncontrolledTooltip>
          </div>

          {/* Volume icon */}
          <div id="volume-icon" className="py-2 cursor-help">
            <FontAwesomeIcon icon={faCube} />
            <UncontrolledTooltip placement="right" target="volume-icon">
              {dictionary.dimensions.volume}
            </UncontrolledTooltip>
          </div>

          {/* Volumetric weight icon */}
          <div id="volumetric-weight-icon" className="py-2 cursor-help">
            <FontAwesomeIcon
              icon={faCalculator}
              className={
                taxed === data.weight || volumetricWeight == null
                  ? "fw-bold"
                  : ""
              }
            />
            <UncontrolledTooltip
              placement="right"
              target="volumetric-weight-icon"
            >
              {`${dictionary.dimensions.volumetric_weight} = ${dictionary.dimensions.weight} * ${conversionFactor}`}
            </UncontrolledTooltip>
          </div>
        </div>

        {/* ======================================================
            COLUMN 2: USER-DECLARED DATA
            - Declared weight
            - Declared volume
            - Calculated volumetric weight
           ====================================================== */}
        <div className="col-5 p-0" style={{ borderRight: "1px solid #c7c9cd" }}>
          {/* Column header */}
          <div
            id="user-icon"
            className="py-2 cursor-help"
            style={{ borderBottom: "1px solid #c7c9cd" }}
          >
            <FontAwesomeIcon icon={faUser} />
            <UncontrolledTooltip placement="top" target="user-icon">
              {dictionary.dimensions.declared_weight} ({dictionary.users.user})
            </UncontrolledTooltip>
          </div>

          {/* User-declared weight */}
          <div
            id="weight-user"
            className={`py-2 ${taxed === data.weight ? "fw-bold cursor-help" : ""}`}
          >
            {data.weight != null ? `${data.weight.toFixed(2)} Kg` : "-"}
            {taxed === data.weight && (
              <UncontrolledTooltip placement="top" target="weight-user">
                {dictionary.dimensions.taxed_weight}{" "}
                {
                  dictionary.messages
                    .the_greater_of_real_weight_or_volumetric_weight
                }
              </UncontrolledTooltip>
            )}
          </div>

          {/* User-declared volume */}
          <div className="py-2">
            {data.volume?.toFixed(6)} m<sup>3</sup>
          </div>

          {/* Volumetric weight calculated from user data */}
          <div
            id="volumetric-weight-user"
            className={`py-2 ${taxed === volumetric ? "fw-bold cursor-help" : ""}`}
          >
            {volumetric != null ? `${volumetric.toFixed(2)} Kg` : "-"}
            {taxed === volumetric && (
              <UncontrolledTooltip
                placement="top"
                target="volumetric-weight-user"
              >
                {dictionary.dimensions.taxed_weight}{" "}
                {
                  dictionary.messages
                    .the_greater_of_real_weight_or_volumetric_weight
                }
              </UncontrolledTooltip>
            )}
          </div>
        </div>

        {/* ======================================================
            COLUMN 3: CARRIER-DETECTED DATA
            - Detected weight
            - Detected volume
            - Detected volumetric weight
            (currently unavailable)
           ====================================================== */}
        <div className="col-5 p-0">
          {/* Column header */}
          <div
            id="carrier-icon"
            className="py-2 cursor-help"
            style={{ borderBottom: "1px solid #c7c9cd" }}
          >
            <FontAwesomeIcon icon={faTruck} />
            <UncontrolledTooltip placement="top" target="carrier-icon">
              {dictionary.dimensions.detected_weight} (
              {dictionary.registries.carrier})
            </UncontrolledTooltip>
          </div>

          {/* Carrier data placeholders */}
          <div className="py-2">-</div>
          <div className="py-2">-</div>
          <div className="py-2">-</div>
        </div>
      </div>
    </div>
  );
};
